
import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, Settings, LogOut, Sparkles, Swords, PlayCircle, Film, Menu, X, PenTool, ShoppingBag, BarChart2, Ticket } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/ui/Logo';
import { supabase } from '../../services/supabaseClient';

const AdminLayout: React.FC = () => {
  const { business, isLoading, language } = useStore();
  const { signOut, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [business, isLoading, authLoading, user, navigate]);
  
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-[#020202] flex flex-col items-center justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-brand-green/20 blur-xl rounded-full animate-pulse"></div>
            <Logo size="lg" className="relative z-10" />
          </div>
          <div className="mt-8 flex flex-col items-center gap-2">
            <p className="text-gray-500 font-mono uppercase tracking-[0.3em] text-[10px]">Loading Studio...</p>
          </div>
      </div>
    );
  }

  if (user && business === null) return <Navigate to="/login" replace />;
  if (!business) return <Navigate to="/setup" replace />;

  return (
    <div className="min-h-screen bg-[#020202] flex flex-col lg:flex-row font-sans text-white selection:bg-brand-green selection:text-black overflow-hidden">
      
      {/* SIDEBAR DESKTOP */}
      <aside className="w-72 hidden lg:flex flex-col z-30 relative m-4 rounded-[2rem] bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/5 shadow-2xl">
        <div className="p-8 flex items-center gap-3">
            <Logo size="md" />
            <div>
                <h1 className="text-lg font-black italic tracking-tighter leading-none text-white">HeroFrog<span className="text-brand-green">.</span></h1>
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">OrderFlow</span>
            </div>
        </div>
        
        <div className="flex-1 px-4 py-4 space-y-1 overflow-y-auto custom-scrollbar">
            <p className="px-4 py-2 text-[9px] font-black text-gray-600 uppercase tracking-widest mt-2 mb-1">GESTÃO</p>
            <NavItem to="/admin/dashboard" icon={BarChart2} label="Relatórios" highlight />
            <NavItem to="/admin/pedidos" icon={ShoppingBag} label="Pedidos" />
            <NavItem to="/admin/menu" icon={PenTool} label="Cardápio" />
            
            <p className="px-4 py-2 text-[9px] font-black text-gray-600 uppercase tracking-widest mt-6 mb-1">MARKETING</p>
            <NavItem to="/admin/marketing" icon={Ticket} label="Cupons & Marketing" />
        </div>

        <div className="p-4 mt-auto">
             <div className="bg-white/5 border border-white/5 p-3 rounded-2xl flex items-center gap-3 hover:bg-white/10 transition-all cursor-pointer group" onClick={() => navigate('/admin/configuracoes')}>
                <div className="relative">
                    {business.logoUrl ? (
                    <img src={business.logoUrl} className="w-10 h-10 rounded-full object-cover border-2 border-brand-green/20" alt="Profile" />
                    ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-800 to-black rounded-full flex items-center justify-center text-brand-green font-bold">
                        {business.username[0].toUpperCase()}
                    </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white truncate">@{business.username}</p>
                    <p className="text-[9px] text-gray-500 font-mono uppercase">Configurações</p>
                </div>
                <Settings size={16} className="text-gray-600 group-hover:text-white transition-colors"/>
             </div>
             <button onClick={signOut} className="mt-3 w-full flex items-center justify-center gap-2 text-[10px] font-bold text-gray-600 hover:text-red-500 py-2 transition-colors uppercase tracking-widest">
                <LogOut size={12} /> Sair
             </button>
        </div>
      </aside>

      {/* MOBILE HEADER */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10 z-50 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
              <Logo size="sm" />
              <span className="font-black italic text-white tracking-tighter">HEROFROG</span>
          </div>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white">
              {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col relative z-10 lg:h-screen lg:overflow-hidden pt-20 lg:pt-0">
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 lg:p-8">
           <Outlet />
        </div>
      </main>

      {/* MOBILE NAV OVERLAY */}
      {isMobileMenuOpen && (
          <div className="fixed inset-0 bg-black/95 z-40 pt-24 px-6 pb-6 lg:hidden flex flex-col overflow-y-auto">
              <div className="space-y-2">
                <NavItem to="/admin/dashboard" icon={BarChart2} label="Dashboard" onClick={() => setIsMobileMenuOpen(false)} />
                <NavItem to="/admin/pedidos" icon={ShoppingBag} label="Pedidos" onClick={() => setIsMobileMenuOpen(false)} />
                <NavItem to="/admin/menu" icon={PenTool} label="Cardápio" onClick={() => setIsMobileMenuOpen(false)} />
                <NavItem to="/admin/configuracoes" icon={Settings} label="Configurações" onClick={() => setIsMobileMenuOpen(false)} />
              </div>
              <button onClick={signOut} className="mt-8 flex items-center gap-2 text-red-500 font-bold uppercase text-xs tracking-widest">
                  <LogOut size={16} /> Logout
              </button>
          </div>
      )}
    </div>
  );
};

const NavItem = ({ to, icon: Icon, label, highlight, onClick }: any) => (
  <NavLink 
    to={to} 
    onClick={onClick}
    className={({isActive}) => `
        group flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 relative overflow-hidden
        ${isActive 
            ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.1)]' 
            : 'text-gray-500 hover:text-white hover:bg-white/5'
        }
    `}
  >
    {({ isActive }) => (
        <>
            <Icon className={`w-4 h-4 transition-transform group-hover:scale-110 ${isActive ? 'text-black' : highlight ? 'text-brand-green' : 'text-gray-400 group-hover:text-white'}`} />
            <span className={`font-bold text-[11px] uppercase tracking-[0.1em] ${isActive ? 'font-black' : ''}`}>{label}</span>
            {isActive && <div className="absolute right-3 w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse"></div>}
        </>
    )}
  </NavLink>
);

export default AdminLayout;
